/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react';
import { jsx, css } from '@emotion/react';
import { colors } from '@atlaskit/theme';
import { queryCache, useQuery } from 'react-query';
import { OnSuggestionSelected } from 'react-autosuggest';
import Textfield from 'components/textfield';
import Textarea from '@atlaskit/textarea';
import Autosuggest, { ItemContainer } from 'components/autosuggest';
import api from 'libs/api';
import { SaleItem } from '../types';
import useSearchable from '../../../hooks/useSearchable';
import { useFormModal } from '../../../components/modal-dialog';
import SaleItemForm from './SaleItemForm';
import useSaleItemModal from './useSaleItemModal';
import { FormattedMessage } from 'components/i18n';
import Button from '@atlaskit/button';

const defaultProps = {
  shouldSuggest: true,
};

type Props = Omit<
  React.ComponentProps<typeof Textarea>,
  'theme' | 'onSelect'
> & {
  // value: string;
  // onChange: (description: string) => void;
  onSelect?: (company: SaleItem) => void;
  autoFocus?: boolean;
} & typeof defaultProps;

const fetchSaleItems = async (key: string) => {
  return api.get('/sale-items');
};


const renderNewSuggestion = (name: string) => (
  <ItemContainer css={css`border-top: 1px solid ${colors.N30}; line-height: 1.5;`}>
    {/*<div css={css` font-weight: 500;`}>{`+ Create item "${name}"`}</div>*/}
    <div css={css` font-weight: 500;`}>{`+ `} <FormattedMessage
      id="sale.SaleItemSuggest.createItem"
      defaultMessage="Create item"
    /></div>
  </ItemContainer>
);

const renderSuggestion = ({ name, description, price , isNew}: SaleItem) => {
  if(isNew) {
    return renderNewSuggestion(name)
  }
  return (
    <ItemContainer>
      <div
        css={css`
          font-weight: 500;
        `}
      >
        {name}
      </div>
      <div
        css={css`
          color: ${colors.subtleText()};
          font-size: 0.8rem;
        `}
      >
        {[description, price].join(' - ')}
      </div>
    </ItemContainer>
  );
};

const getSuggestionValue = (saleItem: SaleItem) => saleItem.name;

const renderInputComponent = (inputProps:any, shouldSuggest:boolean) =><div css={css` textarea {color: ${shouldSuggest? 'inherit' : colors.subtleText()};} textarea:focus {color: inherit};}`}><Textarea {...inputProps} /></div>

const SaleItemSuggest: React.FC<Props> = (props) => {
  const { value, onChange, onSelect, autoFocus, shouldSuggest, ...rest } = props;

  const { data, error, isLoading } = useQuery(['saleItems'], fetchSaleItems, {staleTime: 1000 * 60 * 30 });

  const saleItems = useSearchable(data, value, {
    keys: ['name', 'description']
  }) || [];

  const showSaleItemModal = useSaleItemModal();
  const showNewSaleItemModal = useCallback((item) => {
    showSaleItemModal({
      saleItem: item,
      isNew: true,
      onSubmit: async (saleItem: SaleItem) => {
        if(onSelect) onSelect(saleItem);
      },
    });
  },[]);

  let suggestions = [...saleItems, {name: value, type: 'service', isNew: true}];
  suggestions = shouldSuggest? suggestions : [];
  const handleOnSuggestionSelected = useCallback<
    OnSuggestionSelected<SaleItem>
  >((event, data) => {
    const saleItem = data.suggestion;
    if(saleItem.isNew) {
      showNewSaleItemModal(saleItem)
    } else if(onSelect) onSelect(saleItem);
  }, []);

  return (
    <Autosuggest
      rows={1}
      minimumRows={1}
      {...rest}
      suggestions={(suggestions || []) as SaleItem[]}
      containerWidth={suggestions.length>1? '600px' : '100%'}
      value={value}
      onChange={onChange}
      onSuggestionSelected={handleOnSuggestionSelected}
      renderSuggestion={renderSuggestion}
      getSuggestionValue={getSuggestionValue}
      autoFocus={autoFocus}
      renderInputComponent={(inputProps: any) => renderInputComponent(inputProps, shouldSuggest)}
  />
  );
};

SaleItemSuggest.defaultProps = defaultProps;

export default SaleItemSuggest;
